import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useT } from '../../i18n';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const useStyles = createUseStyles({
  // No plate of its own: this only ever renders inside MenuModal, which is
  // already the plate. Nesting a second one put a groove inside a groove.
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  field: { display: 'flex', flexDirection: 'column', gap: '7px' },
  label: {
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: 700,
    color: '#c4a070',
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    background: '#150d07',
    border: '1px solid #4a3020',
    borderRadius: '8px',
    color: '#f5e6c8',
    padding: '11px 13px',
    fontSize: '14px',
    fontFamily: MONO,
    letterSpacing: '1px',
    outline: 'none',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
    transition: 'border-color 0.15s',
    '&:focus': { borderColor: '#8b4513' },
    '&::placeholder': { color: '#6a4a2a' },
  },
  choices: { display: 'flex', gap: '8px' },
  choice: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    minHeight: '44px',
    padding: '8px 6px',
    background: '#241811',
    border: '1px solid #4a3020',
    borderRadius: '8px',
    cursor: 'pointer',
    fontFamily: MONO,
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#a8875d',
    textAlign: 'center',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
    '&:hover': { color: '#e8d6ae' },
  },
  choiceOn: {
    background: '#3a2416',
    borderColor: '#8b4513',
    color: '#f5e6c8',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -2px 0 rgba(0,0,0,0.3)',
  },
  radio: {
    position: 'absolute',
    opacity: 0,
    width: '1px',
    height: '1px',
  },
  hint: { fontSize: '11.5px', color: '#8a6e4c', lineHeight: 1.5 },
  warn: { fontSize: '11.5px', color: '#e35c36', fontWeight: 700, lineHeight: 1.5 },
  actions: { display: 'flex', gap: '8px' },
  createBtn: {
    flex: 2,
    minHeight: '46px',
    background: '#d94f2b',
    border: '1px solid #120c07',
    borderRadius: '9px',
    color: '#fff3e6',
    fontSize: '13px',
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.3)',
      'inset 0 -3px 0 rgba(0,0,0,0.28)',
      '0 3px 0 #7d2a12',
    ].join(','),
    '&:active:not(:disabled)': {
      transform: 'translateY(3px)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2), inset 0 2px 5px rgba(0,0,0,0.4)',
    },
    '&:disabled': { opacity: 0.4, cursor: 'not-allowed', boxShadow: 'none' },
  },
  cancelBtn: {
    flex: 1,
    minHeight: '46px',
    background: 'none',
    border: '1px solid #6b4622',
    borderRadius: '9px',
    color: '#d99a6a',
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    '&:hover': { borderColor: '#d94f2b', color: '#fff3e6' },
  },
});

/**
 * The Create form talks to no socket. It navigates, carrying the policy in
 * router state, and the server honours it only if that join is what builds the
 * room — which is what makes a room with no players in it unrepresentable.
 *
 * The cost of that is a colliding code silently joining the existing room, so
 * the form checks the live list it is handed and refuses first.
 */
function CreateRoomForm({ initialCode, takenCodes, onCreate, onCancel }) {
  const classes = useStyles();
  const t = useT();
  const [gameId, setGameId] = useState(initialCode);
  const [visibility, setVisibility] = useState('public');
  const [password, setPassword] = useState('');
  const [joinable, setJoinable] = useState(true);

  const code = gameId.trim();
  const taken = takenCodes.has(code);
  const needsPassword = visibility === 'password' && !password.trim();
  const canCreate = Boolean(code) && !taken && !needsPassword;

  const submit = (e) => {
    e.preventDefault();
    if (!canCreate) return;
    onCreate({ gameId: code, create: { visibility, password, joinable } });
  };

  const choice = on => `${classes.choice}${on ? ` ${classes.choiceOn}` : ''}`;

  return (
    <form className={classes.form} onSubmit={submit}>
      <div className={classes.field}>
        <label className={classes.label} htmlFor="create-room-code">{t('menu.roomCode')}</label>
        <input
          id="create-room-code"
          className={classes.input}
          value={gameId}
          onChange={e => setGameId(e.target.value)}
          spellCheck={false}
          autoComplete="off"
        />
        {taken && <span className={classes.warn}>{t('menu.codeTaken')}</span>}
      </div>

      <div className={classes.field}>
        <span className={classes.label}>{t('access.title')}</span>
        <div className={classes.choices} role="radiogroup" aria-label={t('access.title')}>
          <label className={choice(visibility === 'public')}>
            <input
              className={classes.radio}
              type="radio"
              name="visibility"
              checked={visibility === 'public'}
              onChange={() => setVisibility('public')}
              aria-label={t('access.public')}
            />
            {t('access.public')}
          </label>
          <label className={choice(visibility === 'password')}>
            <input
              className={classes.radio}
              type="radio"
              name="visibility"
              checked={visibility === 'password'}
              onChange={() => setVisibility('password')}
              aria-label={t('access.password')}
            />
            🔒 {t('access.password')}
          </label>
        </div>
        {visibility === 'password' && (
          <input
            className={classes.input}
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder={t('access.passwordPlaceholder')}
            aria-label={t('a11y.roomPassword')}
            autoComplete="off"
          />
        )}
      </div>

      <div className={classes.field}>
        <span className={classes.label}>{t('access.newPlayers')}</span>
        <div className={classes.choices} role="radiogroup" aria-label={t('access.newPlayers')}>
          <label className={choice(joinable)}>
            <input
              className={classes.radio}
              type="radio"
              name="joinable"
              checked={joinable}
              onChange={() => setJoinable(true)}
              aria-label={t('access.accepting')}
            />
            {t('access.accepting')}
          </label>
          <label className={choice(!joinable)}>
            <input
              className={classes.radio}
              type="radio"
              name="joinable"
              checked={!joinable}
              onChange={() => setJoinable(false)}
              aria-label={t('access.notAccepting')}
            />
            {t('access.notAccepting')}
          </label>
        </div>
        <span className={classes.hint}>
          {t('access.changeAnyTime')}
        </span>
      </div>

      <div className={classes.actions}>
        <button type="submit" className={classes.createBtn} disabled={!canCreate}>{t('menu.createAction')}</button>
        <button type="button" className={classes.cancelBtn} onClick={onCancel}>{t('menu.cancel')}</button>
      </div>
    </form>
  );
}

export default CreateRoomForm;
